import type { ValidationArgs } from '@vuelidate/core';
import type { ButtonVariants } from '~ds/components/ui/button';

import useVuelidate from '@vuelidate/core';
import { helpers, maxLength, minLength, numeric, required } from '@vuelidate/validators';

import type { VerificationFlowEmits, VerificationResponse, VerificationSteps, VerificationTypes } from '~/types/verification';

import { EVENTS } from '~/constants/events';
import {
  VERIFICATION_STEPS,
  VERIFICATION_TYPES,

} from '~/types/verification';

import { useAttemptVerification } from './useAttemptVerification';
import { useRequestVerification } from './useRequestVerification';

export const useVerification = (verificationType: VerificationTypes, emit: (event: keyof VerificationFlowEmits, ...args: unknown[]) => void, initialPhoneNumber?: string) => {
  const { attemptVerification } = useAttemptVerification();
  const { requestVerification } = useRequestVerification();

  const analytics = useAnalyticsService();
  const { t } = useI18n();

  // const { fetchUser } = useAuth();

  const currentStep = ref<VerificationSteps>(VERIFICATION_STEPS.AUTH_METHOD_INPUT);
  const authMethodValue = ref(initialPhoneNumber ?? '');
  const pincodeValue = ref('');
  const loading = ref(false);
  const errorMessage = ref<string | null>(null);
  const phoneIsValid = ref(!!initialPhoneNumber);

  // Helper function to handle verification response errors
  const handleVerificationResponse = (response: VerificationResponse | null | undefined): string | null => {
    if (!response) {
      return t('general.unexpectedError');
    }
    if (response.error) {
      return getFirstErrorMessageFromCombinedErrors(t, response.error);
    }
    if (!response.success) {
      return t('general.unexpectedError');
    }
    return null;
  };

  // Validation rules
  const validationRules = computed(() => {
    const rules: Record<string, ValidationArgs> = {};

    if (currentStep.value === VERIFICATION_STEPS.AUTH_METHOD_INPUT) {
      if (verificationType === VERIFICATION_TYPES.PHONE) {
        rules.authMethodValue = {
          required: helpers.withMessage(t('validation.required', { attribute: 'Phone number' }), required),
          // Phone validation is handled by the KUICoreInputPhoneNumber component
        };
      }
    }
    else if (currentStep.value === VERIFICATION_STEPS.PINCODE_INPUT) {
      rules.pincodeValue = {
        required: helpers.withMessage(t('validation.required', { attribute: 'Verification code' }), required),
        numeric: helpers.withMessage(t('validation.numeric', { attribute: 'Verification code' }), numeric),
        minLength: helpers.withMessage(t('validation.verificationCodeLength'), minLength(6)),
        maxLength: helpers.withMessage(t('validation.verificationCodeLength'), maxLength(6)),
      };
    }

    return rules;
  });

  const v$ = useVuelidate(validationRules, { authMethodValue, pincodeValue });

  const handleRequestVerificationCode = async () => {
    /** TODO: throttling for 1 min */

    /** can reuse this for re-request */
    const response = await requestVerification({ verificationMethod: verificationType, authMethodValue: authMethodValue.value });
    const error = handleVerificationResponse(response);

    if (error) {
      errorMessage.value = error;
      return;
    }

    analytics.track(EVENTS.PHONE_VERIFICATION_CODE_REQUESTED, {});

    currentStep.value = VERIFICATION_STEPS.PINCODE_INPUT;
  };

  const handleAttemptVerification = async () => {
    analytics.track(EVENTS.PHONE_VERIFICATION_SUBMIT, {});
    const response = await attemptVerification({
      verificationMethod: verificationType,
      authMethodValue: authMethodValue.value,
      pincodeValue: pincodeValue.value,
    });
    const error = handleVerificationResponse(response);

    if (error) {
      errorMessage.value = error;
      return;
    }

    analytics.track(EVENTS.PHONE_VERIFICATION_COMPLETE);
    currentStep.value = VERIFICATION_STEPS.SUCCESS;
  };

  const handleSubmit = async () => {
    errorMessage.value = null;

    if (currentStep.value !== VERIFICATION_STEPS.SUCCESS) {
      const isValid = await v$.value.$validate();
      if (!isValid) {
        return;
      }
      if (verificationType === VERIFICATION_TYPES.PHONE && phoneIsValid.value !== true) {
        return;
      }
    }

    loading.value = true;
    try {
      if (currentStep.value === VERIFICATION_STEPS.AUTH_METHOD_INPUT) {
        await handleRequestVerificationCode();
      }
      else if (currentStep.value === VERIFICATION_STEPS.PINCODE_INPUT) {
        await handleAttemptVerification();
      }
      else if (currentStep.value === VERIFICATION_STEPS.SUCCESS) {
        emit('verified');
      }
    }
    finally {
      loading.value = false;
    }
  };

  const handleWrongAuthMethodEntered = () => {
    authMethodValue.value = '';
    pincodeValue.value = '';
    currentStep.value = VERIFICATION_STEPS.AUTH_METHOD_INPUT;
    phoneIsValid.value = false;
    v$.value.$reset();
  };

  // Resets the OTP step state back to AUTH_METHOD_INPUT but keeps the verified phone value
  // visible — used after a successful verification so the dialog can stay on the input view
  // showing the now-verified number, instead of falling back to the placeholder.
  const handleVerificationSuccessReset = () => {
    pincodeValue.value = '';
    currentStep.value = VERIFICATION_STEPS.AUTH_METHOD_INPUT;
    v$.value.$reset();
  };

  const handlePhoneIsValid = (isValid: boolean) => {
    phoneIsValid.value = isValid;
  };

  const submitButtonProps = computed<{ text: string; variant: Extract<ButtonVariants['variant'], 'secondary' | 'outline'> } | null>(() => {
    if (currentStep.value === VERIFICATION_STEPS.AUTH_METHOD_INPUT) {
      return { text: t('general.verification.sendCode'), variant: 'secondary' };
    }
    if (currentStep.value === VERIFICATION_STEPS.PINCODE_INPUT) {
      return { text: t('general.formFields.submit.label'), variant: 'secondary' };
    }
    if (currentStep.value === VERIFICATION_STEPS.SUCCESS) {
      return { text: t('general.close'), variant: 'outline' };
    }
    return null;
  });

  const isSubmitDisabled = computed<boolean>(() => {
    if (currentStep.value === VERIFICATION_STEPS.AUTH_METHOD_INPUT) {
      // For phone: need valid phone AND no vuelidate errors
      if (verificationType === VERIFICATION_TYPES.PHONE) {
        return !phoneIsValid.value || v$.value.authMethodValue.$invalid;
      }
    }
    if (currentStep.value === VERIFICATION_STEPS.PINCODE_INPUT) {
      return v$.value.pincodeValue.$invalid;
    }
    return false;
  });

  return {
    authMethodValue,
    pincodeValue,
    currentStep,
    loading,
    handleSubmit,
    handleRequestVerificationCode,
    handleWrongAuthMethodEntered,
    handleVerificationSuccessReset,
    submitButtonProps,
    errorMessage,
    v$,
    handlePhoneIsValid,
    isSubmitDisabled,
  };
};
